import { useState, useEffect, useMemo } from 'react';
import { ImageUsageManager, ImageStatus, ImageUsageData } from '../utils/ImageUsageManager';
import { Image } from '../types';

/**
 * Hook to get image usage information for a category
 * @param categoryId The category ID
 * @param images Array of images to enrich with usage data
 */
export const useImageUsage = (categoryId: string | undefined, images: Image[]) => {
  const [usageData, setUsageData] = useState<Record<string, ImageUsageData>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load usage data from persistent storage
  useEffect(() => {
    const loadUsageData = async () => {
      if (!categoryId) {
        setUsageData({});
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await ImageUsageManager.getInstance().getCategoryUsage(categoryId);
        setUsageData(data);
      } catch (error) {
        console.error('Failed to load image usage data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsageData();
  }, [categoryId]);

  // Enrich images with usage data
  const enrichedImages = useMemo(() => {
    if (!images || !images.length) return [];

    return images.map(image => ({
      ...image,
      isUsed: !!usageData[image.id],
      lastUsedTimestamp: usageData[image.id]?.timestamp || undefined,
      status: usageData[image.id]?.status
    }));
  }, [images, usageData]);

  // Sort images by usage (completed images first, then in-progress, then by timestamp)
  const sortedImages = useMemo(() => {
    return [...enrichedImages].sort((a, b) => {
      // First sort completed images to the top
      if (a.status === ImageStatus.COMPLETED && b.status !== ImageStatus.COMPLETED) {
        return -1;
      }
      if (a.status !== ImageStatus.COMPLETED && b.status === ImageStatus.COMPLETED) {
        return 1;
      }
      
      // Then sort by whether it's used or not
      if (!!a.isUsed !== !!b.isUsed) {
        return a.isUsed ? -1 : 1;
      }
      
      // Then sort by last used timestamp (most recent first)
      if (a.lastUsedTimestamp && b.lastUsedTimestamp) {
        return b.lastUsedTimestamp - a.lastUsedTimestamp;
      }
      
      // If one has a timestamp and the other doesn't
      if (a.lastUsedTimestamp) return -1;
      if (b.lastUsedTimestamp) return 1;
      
      // Default sorting (e.g., by ID or name)
      return a.id.localeCompare(b.id);
    });
  }, [enrichedImages]);

  // Function to update image usage
  const updateImageUsage = async (imageId: string) => {
    if (!categoryId) return;
    
    try {
      const timestamp = Date.now();
      await ImageUsageManager.getInstance().updateImageUsage(categoryId, imageId, timestamp);
      
      // Update local state to avoid refetching
      setUsageData(prev => ({
        ...prev,
        [imageId]: {
          timestamp,
          status: ImageStatus.IN_PROGRESS
        }
      }));
    } catch (error) {
      console.error('Failed to update image usage:', error);
    }
  };

  return {
    images: sortedImages,
    isLoading,
    usageData,
    updateImageUsage
  };
};

export default useImageUsage; 